import {
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  Divider,
  FAIconName,
  Icon,
  Tag,
  TagColor,
  Text,
} from '@equip.health/ui';

import CollapsibleUserCard from '~/components/common/CollapsibleUserCard';
import PatientContactDetails from './PatientContactDetails';

interface PatientContactListItemProps {
  contact: PatientContact;
  onAdd: () => void;
  patientId: string;
  hasPatientDashboardPermission: boolean;
  patientBasicDetails: Patient;
  supportEmailAddresses: string[];
}
interface ContactListProps {
  contacts: PatientContact[];
  onAdd: () => void;
  patientId: string;
  hasPatientDashboardPermission: boolean;
  patientBasicDetails: Patient;
  supportEmailAddresses: string[];
}

const PatientContactListItem = ({
  contact,
  onAdd,
  patientBasicDetails,
  patientId,
  supportEmailAddresses,
}: PatientContactListItemProps) => {
  const {
    supportExternalId,
    contactExternalId,
    chosenName,
    firstName,
    isEmergencyContact,
    isGuarantor,
    isPrimaryContact,
    isReferringProvider,
    isOngoingProvider,
    lastName,
    middleName,
    prefix,
    suffix,
    relationToPatient,
    isSupport,
    isProxy,
  } = contact || {};

  const TOP_LEVEL_ID = `patient-contact-list-item__${
    contactExternalId ?? supportExternalId
  }`;

  return (
    <CollapsibleUserCard
      avatarProps={{
        name: `${chosenName || firstName} ${lastName}`,
        src: null,
        userType: 'support',
      }}
      hideTooltip
      id={TOP_LEVEL_ID}
      labels={
        <>
          {isEmergencyContact && (
            <Tag
              leftIcon={FAIconName.messagePlusRegular}
              color={TagColor.red}
              id={`${TOP_LEVEL_ID}__emergency-contact`}
              value="Emergency contact"
            />
          )}
          {(isProxy || isSupport) && (
            <ChakraFlex alignItems="center" gap="6px">
              <Icon color="accent.blue" name={FAIconName.laptopMobileRegular} />
              {isProxy ? (
                <Text color="accent.blue" variant="captionMedium">
                  Proxy
                </Text>
              ) : (
                isSupport && (
                  <Text color="accent.blue" variant="captionMedium">
                    General
                  </Text>
                )
              )}
            </ChakraFlex>
          )}
        </>
      }
      nameProps={{
        chosenName,
        firstName,
        lastName,
        middleName,
        prefix,
        suffix,
      }}
      role={relationToPatient}
      tags={
        <>
          {isPrimaryContact && (
            <Tag
              color={TagColor.marigold}
              id={`${TOP_LEVEL_ID}__primary`}
              value="Primary"
            />
          )}
          {isGuarantor && (
            <Tag
              color={TagColor.purple}
              id={`${TOP_LEVEL_ID}__guarantor`}
              value="Guarantor"
            />
          )}
          {isReferringProvider && (
            <Tag
              color={TagColor.pink}
              id={`${TOP_LEVEL_ID}__referring-provider`}
              value="Referring provider"
            />
          )}
          {isOngoingProvider && (
            <Tag
              color={TagColor.brown}
              id={`${TOP_LEVEL_ID}__ongoing-provider`}
              value="Ongoing provider"
            />
          )}
        </>
      }
    >
      <ChakraBox marginTop="10px">
        <Divider id={`${TOP_LEVEL_ID}__contact-details`} />
        <PatientContactDetails
          contact={contact}
          onAdd={onAdd}
          patientBasicDetails={patientBasicDetails}
          patientId={patientId}
          supportEmailAddresses={supportEmailAddresses}
        />
      </ChakraBox>
    </CollapsibleUserCard>
  );
};

const ContactList = ({
  contacts,
  hasPatientDashboardPermission,
  onAdd,
  patientId,
  patientBasicDetails,
  supportEmailAddresses,
}: ContactListProps) => {
  return (
    <ChakraSimpleGrid gap="12px">
      {(contacts ?? []).map((contact) => (
        <ChakraGridItem key={contact.supportExternalId}>
          <PatientContactListItem
            contact={contact}
            hasPatientDashboardPermission={hasPatientDashboardPermission}
            onAdd={onAdd}
            patientBasicDetails={patientBasicDetails}
            patientId={patientId}
            supportEmailAddresses={supportEmailAddresses}
          />
        </ChakraGridItem>
      ))}
    </ChakraSimpleGrid>
  );
};

export default ContactList;
